import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ArrowRight, ShieldCheck, Clock, Wallet, ChevronRight, Star } from 'lucide-react';
import { MAJOR_CITIES } from '@/lib/locations';
import { supabase, BlogPost } from '@/lib/supabase';
import { BlogCard } from '@/components/blog/BlogCard';

export function HomePage() {
  const [searchType, setSearchType] = useState<'rent' | 'sale'>('rent');
  const [location, setLocation] = useState('');
  const [suggestions, setSuggestions] = useState<typeof MAJOR_CITIES>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Cargar artículos destacados (Ordenados por fecha para mostrar los más nuevos)
  useEffect(() => {
    async function fetchFeaturedPosts() {
        try {
            const { data } = await supabase
                .from('blog_posts')
                .select('*, categories:blog_categories(name, slug)')
                .eq('status', 'published')
                .eq('is_featured', true)
                .order('published_at', { ascending: false }) // IMPORTANTE: Ordenar por fecha
                .limit(3);
            
            if (data && data.length > 0) {
                setFeaturedPosts(data);
            }
        } catch (error) {
            console.error('Error fetching featured posts:', error);
        }
    }
    fetchFeaturedPosts();
  }, []);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/search?type=${searchType}&city=${encodeURIComponent(location)}`);
  };

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocation(val);
    
    if (val.length > 1) {
      const filtered = MAJOR_CITIES.filter(c => 
        c.name.toLowerCase().includes(val.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSelectCity = (cityName: string) => {
    setLocation(cityName);
    setShowSuggestions(false);
  };

  return (
    <div className="flex flex-col w-full font-sans">
      {/* 1. HERO SECTION (Cinematográfica) */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        {/* Imagen de Fondo de Alta Calidad */}
        <div className="absolute inset-0 z-0">
            <img 
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=2000" 
                alt="Trastero moderno y limpio" 
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"></div>
        </div>
        
        <div className="relative z-10 w-full max-w-4xl px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight drop-shadow-lg">
            Gana espacio en tu vida
          </h1>
          <p className="text-lg md:text-2xl text-slate-100 mb-10 max-w-2xl mx-auto drop-shadow-md font-medium">
            Encuentra el trastero perfecto cerca de ti o rentabiliza tu espacio extra sin comisiones.
          </p>

          {/* Glassmorphism Search Box */}
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-3xl shadow-2xl max-w-3xl mx-auto border border-white/20">
            <div className="bg-white rounded-2xl p-2 shadow-inner">
                <div className="flex gap-2 mb-2 px-2">
                <button 
                    onClick={() => setSearchType('rent')}
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                    searchType === 'rent' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                >
                    Alquilar
                </button>
                <button 
                    onClick={() => setSearchType('sale')}
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                    searchType === 'sale' ? 'bg-blue-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                >
                    Comprar
                </button>
                </div>
                
                <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2">
                <div className="flex-1 relative" ref={wrapperRef}>
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-600 h-5 w-5" />
                    <input 
                    type="text" 
                    placeholder="¿Dónde buscas espacio? (Ej: Madrid)" 
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-slate-900 font-medium placeholder:text-slate-400"
                    value={location}
                    onChange={handleLocationChange}
                    autoComplete="off"
                    />
                    
                    {/* Sugerencias Autocomplete */}
                    {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-50 text-left">
                        {suggestions.map((city) => (
                        <button
                            key={`${city.name}-${city.province}`}
                            type="button"
                            className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center justify-between group transition-colors"
                            onClick={() => handleSelectCity(city.name)}
                        >
                            <span className="font-medium text-slate-700 group-hover:text-blue-700">{city.name}</span>
                            <span className="text-xs text-slate-400 group-hover:text-blue-400">{city.province}</span>
                        </button>
                        ))}
                    </div>
                    )}
                </div>
                <button 
                    type="submit"
                    className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-4 rounded-xl font-bold transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                    <Search className="h-5 w-5" />
                    Buscar
                </button>
                </form>
            </div>
          </div>
        </div>
      </section>

      {/* 2. DESTINOS POPULARES (Visual Grid) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-end mb-10">
                <div>
                    <h2 className="text-3xl font-bold text-slate-900 mb-2">Destinos Populares</h2>
                    <p className="text-slate-500">Las ciudades donde más gente busca espacio.</p>
                </div>
                <Link to="/search" className="text-blue-600 font-bold hover:underline flex items-center gap-1">
                    Ver todas <ChevronRight className="w-4 h-4" />
                </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { name: 'Madrid', img: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?auto=format&fit=crop&q=80&w=600' },
                    { name: 'Barcelona', img: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?auto=format&fit=crop&q=80&w=600' },
                    { name: 'Valencia', img: 'https://images.dualite.app/679fb54c-9f04-437a-8c84-56b50dab032e/pexels-joaquin-carfagna-3131171-16674382-042ef2ea-5f3c-44c2-aa54-b8d304d4763f.webp' },
                    { name: 'Sevilla', img: 'https://images.dualite.app/679fb54c-9f04-437a-8c84-56b50dab032e/pexels-jcosta-20877121-ef27c4c5-7938-4518-ae1a-0bfa7b36886e.webp' }
                ].map((city) => (
                    <Link 
                        key={city.name} 
                        to={`/search?city=${city.name}`}
                        className="group relative h-64 rounded-2xl overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all"
                    >
                        <div className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/10 transition-colors z-10"></div>
                        <img 
                            src={city.img} 
                            alt={`Trasteros en ${city.name}`} 
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute bottom-0 left-0 p-6 z-20 w-full bg-gradient-to-t from-black/80 to-transparent">
                            <h3 className="text-white text-xl font-bold">{city.name}</h3>
                            <p className="text-slate-200 text-sm flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-2 group-hover:translate-y-0">
                                Ver trasteros <ArrowRight className="w-3 h-3" />
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
      </section>

      {/* 3. MAGAZINE & CONSEJOS (Featured Blog) */}
      {featuredPosts.length > 0 && (
        <section className="py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <span className="text-blue-600 font-bold tracking-wider uppercase text-sm">Magazine</span>
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">Aprende a rentabilizar tu espacio</h2>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Consejos de expertos sobre precios, inversión y organización de trasteros.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {featuredPosts.map(post => (
                        <BlogCard key={post.id} post={post} />
                    ))}
                </div>
                
                <div className="text-center mt-12">
                    <Link 
                        to="/blog" 
                        className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white border border-slate-200 text-slate-700 font-bold rounded-full hover:bg-slate-100 hover:text-blue-600 transition-colors shadow-sm"
                    >
                        Leer más artículos <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
      )}

      {/* 4. SPLIT CTA (Publicar) */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-slate-900 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
                <div className="md:w-1/2 p-10 md:p-16 flex flex-col justify-center text-left">
                    <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-1.5 rounded-full text-sm font-bold w-fit mb-6 border border-blue-500/30">
                        <Star className="w-4 h-4 fill-blue-300" /> Publica Gratis
                    </div>
                    <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
                        Convierte tus metros vacíos en <span className="text-blue-400">ingresos extra</span>.
                    </h2>
                    <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                        Miles de personas buscan un lugar para guardar sus cosas en tu zona. 
                        Publicar tu anuncio te llevará menos de 2 minutos y es totalmente gratuito.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Link 
                            to="/publish" 
                            className="bg-blue-600 hover:bg-blue-500 text-white text-lg font-bold py-4 px-8 rounded-xl transition-all text-center"
                        >
                            Publicar mi trastero
                        </Link>
                        <Link 
                            to="/promocion-publica-facil" 
                            className="bg-transparent border border-slate-600 hover:bg-slate-800 text-white text-lg font-bold py-4 px-8 rounded-xl transition-all text-center"
                        >
                            Cómo funciona
                        </Link>
                    </div>
                </div>
                <div className="md:w-1/2 relative min-h-[300px] md:min-h-full">
                    <img 
                        src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&q=80&w=1000" 
                        alt="Persona feliz usando el móvil" 
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-transparent to-transparent md:bg-gradient-to-l"></div>
                </div>
            </div>
        </div>
      </section>

      {/* 5. FEATURES (Trust) */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-blue-600 mb-6">
                <ShieldCheck className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Seguridad Total</h3>
              <p className="text-slate-600 leading-relaxed">
                Verificamos cada anuncio para garantizar que tratas con personas reales y espacios de confianza.
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-green-600 mb-6">
                <Wallet className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Sin Comisiones</h3>
              <p className="text-slate-600 leading-relaxed">
                Lo que ves es lo que pagas. El trato es directo entre particular y propietario, sin intermediarios.
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center text-purple-600 mb-6">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Rápido y Fácil</h3>
              <p className="text-slate-600 leading-relaxed">
                Encuentra lo que buscas en segundos con nuestros filtros avanzados por tamaño, precio y zona.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
